import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import {
    EditorialApprovalToggleInput,
    EditorialAssignmentsUpdateInput,
    EditorialCommentCreateInput,
    EditorialCreateInput,
    EditorialFilesAttachInput,
    EditorialIdVersionInput,
    EditorialMoveInput,
    EditorialSuggestionReviewInput,
    EditorialTaskToggleInput,
    EditorialTransitionInput,
    EditorialUpdateInput,
} from '@libs/types/src/services/input-type/editorial/editorial.input';
import { EditorialApi } from './editorial-api.token';
import {
    EditorialItemObject,
    EditorialUserObject,
} from '@libs/types/src/services/object-type/editorial/editorial.object';

const EDITORIAL_ITEM_FRAGMENT = `
    id version title perex body format state lane publicationAt deadlineAt category tags
    assignments { role userId userName }
    approvals { type approvedByUserId approvedBy approvedAt }
    tasks { id label ownerRole dueAt required status }
    suggestions { id authorUserId author original replacement rationale status }
    comments { id userId author text createdAt }
    assetFileIds workingUrl finalUrl youtubeUrl note
    wordpress { postId postUrl lastSyncedAt }
    auditEvents { id action actorUserId actorName details createdAt }
    createdAt updatedAt
`;

@Injectable({ providedIn: 'root' })
export class EditorialGqlService extends GqlServiceV2 implements EditorialApi {
    public listEditorialItems(): Promise<EditorialItemObject[]> {
        return this.query(
            `query { editorial { list { ${EDITORIAL_ITEM_FRAGMENT} } } }`,
            {},
            { offline: false, errorPolicy: 'none' },
        );
    }

    public listEditorialUsers(): Promise<EditorialUserObject[]> {
        return this.query(
            'query { editorial { listUsers { id name } } }',
            {},
            { offline: false, errorPolicy: 'none' },
        );
    }

    public createEditorialItem(
        input: EditorialCreateInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation('create', 'EditorialCreateInput!', input);
    }

    public updateEditorialItem(
        input: EditorialUpdateInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation('update', 'EditorialUpdateInput!', input);
    }

    public updateEditorialAssignments(
        input: EditorialAssignmentsUpdateInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation(
            'updateAssignments',
            'EditorialAssignmentsUpdateInput!',
            input,
        );
    }

    public moveEditorialItem(
        input: EditorialMoveInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation('move', 'EditorialMoveInput!', input);
    }

    public transitionEditorialItem(
        input: EditorialTransitionInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation(
            'transition',
            'EditorialTransitionInput!',
            input,
        );
    }

    public toggleEditorialTask(
        input: EditorialTaskToggleInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation(
            'toggleTask',
            'EditorialTaskToggleInput!',
            input,
        );
    }

    public toggleEditorialApproval(
        input: EditorialApprovalToggleInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation(
            'toggleApproval',
            'EditorialApprovalToggleInput!',
            input,
        );
    }

    public reviewEditorialSuggestion(
        input: EditorialSuggestionReviewInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation(
            'reviewSuggestion',
            'EditorialSuggestionReviewInput!',
            input,
        );
    }

    public addEditorialComment(
        input: EditorialCommentCreateInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation(
            'addComment',
            'EditorialCommentCreateInput!',
            input,
        );
    }

    public attachEditorialFiles(
        input: EditorialFilesAttachInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation(
            'attachFiles',
            'EditorialFilesAttachInput!',
            input,
        );
    }

    public syncEditorialWordpress(
        input: EditorialIdVersionInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation(
            'syncWordpress',
            'EditorialIdVersionInput!',
            input,
        );
    }

    public publishEditorialWordpress(
        input: EditorialIdVersionInput,
    ): Promise<EditorialItemObject> {
        return this.itemMutation(
            'publishFromWordpress',
            'EditorialIdVersionInput!',
            input,
        );
    }

    private itemMutation<T extends object>(
        field: string,
        type: string,
        input: T,
    ): Promise<EditorialItemObject> {
        return this.mutate(
            `
            mutation($input: ${type}) {
                editorial { ${field}(input: $input) { ${EDITORIAL_ITEM_FRAGMENT} } }
            }
        `,
            { input },
            { offline: false },
        );
    }
}
